import * as THREE from "three";
import { REST, bendOf, samplePath, type Affine, type Color, type Form, type Palette, type Pose, type RigModel, type RigPart, type Shape } from "./Rig";
import { BEND_GRID, bake, type Placed } from "./RigCanvas";

/** Depth between drawing orders: enough that flat parts never fight. */
const Z = 0.004;
/** How finely parts are baked (pixels a block). */
const PX = 48;

/**
 * A rig in the game: each run of still parts baked into one textured quad,
 * each moving part baked into its own on a node placed as posed (a bent
 * one on a grid that curls with it); glowing shapes kept as geometry so
 * they can bloom. A whale is a handful of quads instead of thirty meshes.
 */
export class RigMesh {
  readonly group = new THREE.Group();
  /** Each part's frame, placed straight in the rig's from its affine. */
  private readonly nodes: THREE.Object3D[] = [];
  /** Bent parts' grids: where each corner sits unbent, to bend afresh each pose. */
  private readonly bent: { index: number; geometry: THREE.BufferGeometry; rest: Float32Array }[] = [];
  private readonly tinted: THREE.MeshBasicMaterial[] = [];
  /** The glowing shapes' materials, by colour name, so a glow can change (a whale's spots burning red). */
  private readonly glows = new Map<string, (THREE.MeshBasicMaterial | THREE.LineBasicMaterial)[]>();
  private readonly trash: { dispose(): void }[] = [];

  constructor(
    private readonly model: RigModel,
    palette: Palette,
    px = PX,
  ) {
    const rest = model.affines(REST);
    for (const p of model.parts) {
      const n = new THREE.Object3D();
      n.matrixAutoUpdate = false;
      place(n, rest[p.index]!);
      this.group.add(n);
      this.nodes.push(n);
    }
    const plain = (p: RigPart) => p.part.shapes.filter((s) => !model.glow.has(s.color));
    for (const layer of model.layers()) {
      if (layer.kind === "part") {
        const p = layer.part;
        this.quad(bake(plain(p).map((shape): Placed => ({ shape, affine: [1, 0, 0, 1, 0, 0] })), palette, bakePx(plain(p), px)), this.nodes[p.index]!, p.part.z, plain(p).some(isPixels), p.part.spine ? p.index : -1);
      } else {
        const items: Placed[] = [];
        for (const p of layer.parts) for (const shape of plain(p)) items.push({ shape, affine: rest[p.index]! });
        this.quad(bake(items, palette, bakePx(items.map((i) => i.shape), px)), this.group, layer.z, items.some((i) => isPixels(i.shape)));
      }
    }
    for (const p of model.parts) for (const s of p.part.shapes) if (model.glow.has(s.color)) this.nodes[p.index]!.add(this.glowing(s, palette, p.part.z));
  }

  /** A baked picture on a quad under `under`; for a bent part (`bent`, its index) a grid, its corners kept unbent to bend later. */
  private quad(baked: ReturnType<typeof bake>, under: THREE.Object3D, z: number, pixel: boolean, bent = -1): void {
    if (!baked) return;
    const { canvas, bounds } = baked;
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    if (pixel) {
      // Pixel art keeps its hard edges.
      texture.magFilter = THREE.NearestFilter;
      texture.minFilter = THREE.NearestFilter;
      texture.generateMipmaps = false;
    }
    const n = bent < 0 ? 1 : BEND_GRID;
    const geometry = new THREE.PlaneGeometry(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, n, n);
    const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false });
    const mesh = new THREE.Mesh(geometry, material);
    if (bent < 0) mesh.position.set((bounds.minX + bounds.maxX) / 2, (bounds.minY + bounds.maxY) / 2, z * Z);
    else {
      geometry.translate((bounds.minX + bounds.maxX) / 2, (bounds.minY + bounds.maxY) / 2, 0);
      mesh.position.z = z * Z;
      this.bent.push({ index: bent, geometry, rest: Float32Array.from(geometry.attributes.position!.array) });
    }
    under.add(mesh);
    this.tinted.push(material);
    this.trash.push(texture, geometry, material);
  }

  /** A glowing shape as geometry, brighter than white and never fogged; see-through ones add to what's behind. */
  private glowing(s: Shape, palette: Palette, z: number): THREE.Object3D {
    const color = palette[s.color];
    if (!color) throw new Error(`rig: no colour ${s.color} in the palette`);
    const [ox, oy] = s.at ?? [0, 0];
    const blend = s.alpha !== undefined && s.alpha < 1 ? { transparent: true, opacity: s.alpha, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide } : {};
    let o: THREE.Object3D;
    let material: THREE.MeshBasicMaterial | THREE.LineBasicMaterial;
    if (s.form.kind === "stroke") {
      const geometry = new THREE.BufferGeometry().setFromPoints(samplePath(s.form.ops).map(([x, y]) => new THREE.Vector3(x, y, 0)));
      material = new THREE.LineBasicMaterial({ color: new THREE.Color(...color), fog: false, ...blend });
      o = new THREE.Line(geometry, material);
      this.trash.push(geometry);
    } else {
      if (s.form.kind === "pixels") throw new Error("rig: pixel art can't glow");
      const geometry = new THREE.ShapeGeometry(toShapes(s.form), 16);
      material = new THREE.MeshBasicMaterial({ color: new THREE.Color(...color), fog: false, ...blend });
      o = new THREE.Mesh(geometry, material);
      this.trash.push(geometry);
    }
    this.trash.push(material);
    const list = this.glows.get(s.color) ?? [];
    list.push(material);
    this.glows.set(s.color, list);
    o.position.set(ox, oy, z * Z);
    return o;
  }

  /** Place every part as `pose` says, lit by `light` (0..1; glows ignore it). */
  pose(pose: Pose, light: number): void {
    const affines = this.model.affines(pose);
    for (const p of this.model.parts) place(this.nodes[p.index]!, affines[p.index]!);
    for (const { index, geometry, rest } of this.bent) {
      const bend = bendOf(this.model.parts[index]!.part, pose);
      const at = geometry.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < at.count; i++) {
        const [x, y] = bend ? bend.point(rest[i * 3]!, rest[i * 3 + 1]!) : [rest[i * 3]!, rest[i * 3 + 1]!];
        at.setXY(i, x, y);
      }
      at.needsUpdate = true;
      geometry.computeBoundingSphere();
    }
    for (const m of this.tinted) m.color.setScalar(light);
  }

  /** Change what a glowing colour is (only glows can change once baked). */
  setGlow(name: string, color: Color): void {
    for (const m of this.glows.get(name) ?? []) m.color.setRGB(...color);
  }

  dispose(): void {
    for (const t of this.trash) t.dispose();
  }
}

/** A filled form as three.js shapes (a path with several pieces is several). */
function toShapes(f: Exclude<Form, { kind: "stroke" | "pixels" }>): THREE.Shape[] {
  if (f.kind === "path") {
    const shapes: THREE.Shape[] = [];
    let s: THREE.Shape | null = null;
    for (const op of f.ops) {
      if (op[0] === "M") {
        s = new THREE.Shape();
        shapes.push(s);
        s.moveTo(op[1], op[2]);
      } else if (!s) continue;
      else if (op[0] === "L") s.lineTo(op[1], op[2]);
      else if (op[0] === "Q") s.quadraticCurveTo(op[1], op[2], op[3], op[4]);
      else if (op[0] === "C") s.bezierCurveTo(op[1], op[2], op[3], op[4], op[5], op[6]);
      else s.closePath();
    }
    return shapes;
  }
  const s = new THREE.Shape();
  if (f.kind === "disc") {
    s.absellipse(0, 0, f.r, f.ry ?? f.r, 0, Math.PI * 2, false, f.rot ?? 0);
  } else {
    const a = Math.atan2(f.to[1], f.to[0]);
    const r = f.width / 2;
    s.absarc(0, 0, r, a + Math.PI / 2, a + Math.PI * 1.5, false);
    s.absarc(f.to[0], f.to[1], r, a - Math.PI / 2, a + Math.PI / 2, false);
    s.closePath();
  }
  return [s];
}

/** Put a node where an affine says (in its own plane; z is left to what's under it). */
function place(n: THREE.Object3D, m: Affine): void {
  n.matrix.set(m[0], m[2], 0, m[4], m[1], m[3], 0, m[5], 0, 0, 1, 0, 0, 0, 0, 1);
  n.matrixWorldNeedsUpdate = true;
}

const isPixels = (s: Shape) => s.form.kind === "pixels";

/** How finely to bake shapes: pixel art at a whole number of picture pixels to each of its own (so they all come out the same size), anything else at `px`. */
function bakePx(shapes: readonly Shape[], px: number): number {
  const art = shapes.find((s) => s.form.kind === "pixels")?.form;
  return art?.kind === "pixels" ? art.ppu * Math.max(1, Math.round(px / art.ppu)) : px;
}
